import { useState } from "react";
import { HeartHandshake, MessageCircle, ShieldCheck } from "lucide-react";
import { WA_SESSION } from "@/lib/api";

export default function PartnerModule({ content, code }) {
  const storageKey = `partner_module_${code}`;
  const [answers, setAnswers] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "{}"); } catch { return {}; }
  });

  const choose = (sectionIndex, choiceIndex) => {
    const next = { ...answers, [sectionIndex]: choiceIndex };
    setAnswers(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* Keep selection in memory if storage is unavailable. */ }
  };

  return (
    <section data-testid="partner-module" className="mt-10 rounded-[2rem] bg-[var(--cream-2)] p-5 sm:p-8">
      <div className="max-w-3xl">
        <p className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--sage-deep)]"><HeartHandshake size={18} /> Módulo complementario</p>
        <h2 className="font-display text-3xl sm:text-4xl mt-2">{content.title}</h2>
        <p className="mt-3 text-[#5a4f46]">{content.intro}</p>
      </div>

      <div className="mt-8 space-y-5">
        {content.sections.map((section, sectionIndex) => (
          <article key={section.title} className="rounded-2xl bg-white p-5 sm:p-6" data-testid={`partner-section-${sectionIndex}`}>
            <h3 className="font-display text-2xl">{section.title}</h3>
            <p className="mt-3 text-sm text-[#4a4038]">{section.lesson}</p>
            <details className="mt-4 rounded-xl bg-[var(--cream)] p-4">
              <summary className="cursor-pointer font-semibold">Leer ejemplo práctico</summary>
              <p className="mt-3 text-sm leading-relaxed text-[#4a4038]">{section.example}</p>
            </details>
            <div className="mt-4 rounded-xl border border-[#eadfce] p-4" data-testid={`partner-scenario-${sectionIndex}`}>
              <p className="text-xs font-semibold uppercase text-[var(--sage-deep)]">Ensayo interactivo</p>
              <p className="mt-2 text-sm font-semibold">{section.scenario.situation}</p>
              <div className="mt-3 space-y-2" role="group" aria-label={`Respuestas para ${section.title}`}>
                {section.scenario.choices.map((choice, choiceIndex) => (
                  <button key={choice.text} type="button" aria-pressed={answers[sectionIndex] === choiceIndex}
                    data-testid={`partner-choice-${sectionIndex}-${choiceIndex}`}
                    onClick={() => choose(sectionIndex, choiceIndex)}
                    className={`w-full rounded-xl border-2 p-3 text-left text-sm transition-colors ${answers[sectionIndex] === choiceIndex ? "border-[var(--sage-deep)] bg-[var(--cream)]" : "border-[#eadfce] hover:bg-[var(--cream)]"}`}>
                    {choice.text}
                  </button>
                ))}
              </div>
              {Number.isInteger(answers[sectionIndex]) && (
                <p role="status" data-testid={`partner-feedback-${sectionIndex}`} className="mt-3 rounded-xl border-l-4 border-[var(--sage-deep)] bg-[var(--cream)] p-3 text-sm text-[#4a4038]">
                  {section.scenario.choices[answers[sectionIndex]].feedback}
                </p>
              )}
            </div>
            <p className="mt-4 rounded-xl bg-[#E8F0E4] p-4 text-sm text-[#4a4038]"><strong>Prueba para ti:</strong> {section.practice}</p>
          </article>
        ))}
      </div>

      <p className="mt-5 flex gap-2 rounded-xl border border-[var(--terra)]/40 bg-white p-4 text-sm text-[#4a4038]" data-testid="partner-safety-note">
        <ShieldCheck className="shrink-0 text-[var(--sage-deep)]" size={18} /> {content.safety_note}
      </p>
      <a href={WA_SESSION} target="_blank" rel="noreferrer" data-testid="partner-therapy-booking" className="btn-ghost mt-4">
        <MessageCircle size={18} /> Reservar sesión con la psicóloga sanitaria
      </a>
      <p className="mt-2 text-xs text-[#6b5c50]">Las respuestas elegidas se guardan solo en este dispositivo.</p>
    </section>
  );
}